import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Info, Settings, Download, Copy, ExternalLink, Globe2, Lock, Share2, X } from 'lucide-react'
import Layout from '../../components/Layout'
import { valuationService } from '../../services/valuationService'
import ComparablePropertyModal from './ComparablePropertyModal'
import PublicValuationLayout from './PublicValuationLayout'
import ValuationComparablesSection from './ValuationComparablesSection'
import ListingMap from '../../components/ListingMap'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import {
  DEFAULT_SEARCH_RADIUS_METERS,
  MAX_SEARCH_DIAMETER_METERS,
  MIN_SEARCH_DIAMETER_METERS,
  SEARCH_DIAMETER_STEP_METERS,
  getValuationSearchRadius,
  getValuationUsedSearchRadius,
} from '../../helpers/valuationSearchRadius'
import {
  SIMILARITY_METHOD_OPTIONS,
  VALUATION_METHODS,
  getValuationMethodLabel,
  isSimilarityMethod,
  normalizeSimilarityMethod,
} from '../../helpers/valuationMethods'

const waitForImages = async (element, timeoutMs = 5000) => {
  const images = [...element.querySelectorAll('img')]
  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          if (image.complete) {
            resolve()
            return
          }

          let timeoutId
          const finish = () => {
            clearTimeout(timeoutId)
            image.removeEventListener('load', finish)
            image.removeEventListener('error', finish)
            resolve()
          }
          image.addEventListener('load', finish, { once: true })
          image.addEventListener('error', finish, { once: true })
          timeoutId = setTimeout(finish, timeoutMs)
        })
    )
  )
}

const getOwnerShareUrl = (valuation) => {
  if (!valuation?.publicShare?.enabled) return null
  if (valuation.publicShare.publicUrl) return valuation.publicShare.publicUrl
  if (!valuation.publicShare.token || typeof window === 'undefined') return null
  return `${window.location.origin}/v/${valuation.publicShare.token}`
}

const ValuationDetail = ({ viewMode = 'private' }) => {
  const publicMode = viewMode !== 'private'
  const navigate = useNavigate()
  const location = useLocation()
  const { valuationId, shareToken } = useParams()
  const [valuation, setValuation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('main')
  const [recalculatingPropertyId, setRecalculatingPropertyId] = useState(null)
  const [recalculationError, setRecalculationError] = useState(null)
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)
  const [shareError, setShareError] = useState(null)
  const [shareCopied, setShareCopied] = useState(false)
  const [editingSearchDiameter, setEditingSearchDiameter] = useState(false)
  const [searchDiameterMeters, setSearchDiameterMeters] = useState(DEFAULT_SEARCH_RADIUS_METERS * 2)
  const [highlightedComparableId, setHighlightedComparableId] = useState(null)
  const [selectedComparable, setSelectedComparable] = useState(null)
  const [analysisInvalidated, setAnalysisInvalidated] = useState(false)
  const printRef = useRef()

  const fetchValuation = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = publicMode
          ? await valuationService.getPublicValuation(shareToken)
          : await valuationService.getValuation(valuationId)
      setValuation(response)
      setAnalysisInvalidated(false)
      setSearchDiameterMeters(getValuationSearchRadius(response) * 2)
    } catch (error) {
      console.error('Error fetching valuation:', error)
      setError(publicMode ? error.message : 'Chyba při načítání odhadu')
    } finally {
      setLoading(false)
    }
  }, [publicMode, shareToken, valuationId])

  useEffect(() => {
    fetchValuation()
  }, [fetchValuation])

  const recalculateValuation = async ({
    excludedPropertyIds = valuation.excludedPropertyIds || [],
    comparableWeightOverrides = valuation.comparableWeightOverrides || {},
    comparableOverrides = valuation.comparableOverrides || {},
    searchRadiusMeters = getValuationSearchRadius(valuation),
    calculationMethod = valuation.calculationMethod || VALUATION_METHODS.LADDER,
    actionId = 'reset',
  }) => {
    try {
      const invalidatesExistingAnalysis = Boolean(valuation.analysis) || analysisInvalidated
      setRecalculatingPropertyId(actionId)
      setRecalculationError(null)
      const updatedValuation = await valuationService.recalculateValuation(
        valuationId,
        excludedPropertyIds,
        calculationMethod,
        comparableWeightOverrides,
        searchRadiusMeters,
        comparableOverrides
      )
      setValuation(updatedValuation)
      setAnalysisInvalidated(invalidatesExistingAnalysis)
      setSearchDiameterMeters(getValuationSearchRadius(updatedValuation) * 2)
      return updatedValuation
    } catch (error) {
      console.error('Error recalculating valuation:', error)
      setRecalculationError(error.message || 'Chyba při přepočítání odhadu')
      return null
    } finally {
      setRecalculatingPropertyId(null)
    }
  }

  const handleIgnoreProperty = (propertyId) => {
    const excludedPropertyIds = [...new Set([...(valuation.excludedPropertyIds || []), propertyId])]
    const comparableWeightOverrides = { ...(valuation.comparableWeightOverrides || {}) }
    delete comparableWeightOverrides[propertyId]
    recalculateValuation({ excludedPropertyIds, comparableWeightOverrides, actionId: propertyId })
  }

  const handleResetExclusions = () => {
    recalculateValuation({ excludedPropertyIds: [], actionId: 'reset-exclusions' })
  }

  const handleWeightOverride = (property, action) => {
    if (!property.propertyId || !isSimilarityMethod(valuation.calculationMethod)) return

    const overrides = { ...(valuation.comparableWeightOverrides || {}) }
    const naturalWeight = property.calculatedRelevanceWeight ?? property.relevanceWeight ?? property.timeWeight ?? 0.05
    const currentWeight = overrides[property.propertyId] ?? naturalWeight

    if (action === 'auto') {
      delete overrides[property.propertyId]
    } else {
      const delta = action === 'increase' ? 0.1 : action === 'decrease' ? -0.1 : 0
      const nextWeight = action === 'force' ? 1 : Math.min(1, Math.max(0.05, currentWeight + delta))
      overrides[property.propertyId] = Math.round(nextWeight * 10_000) / 10_000
    }

    recalculateValuation({
      comparableWeightOverrides: overrides,
      actionId: `weight:${property.propertyId}`,
    })
  }

  const handleResetWeightOverrides = () => {
    recalculateValuation({ comparableWeightOverrides: {}, actionId: 'reset-weights' })
  }

  const handleMethodChange = (calculationMethod) => {
    if (isSimilarityMethod(valuation.calculationMethod) && calculationMethod === normalizeSimilarityMethod(valuation.calculationMethod)) return
    recalculateValuation({ calculationMethod, actionId: `method:${calculationMethod}` })
  }

  const handleOpenComparable = useCallback((property) => {
    setSelectedComparable(property)
  }, [])

  const handleComparableOverride = async (propertyId, override) => {
    const comparableOverrides = { ...(valuation.comparableOverrides || {}) }
    if (override) comparableOverrides[propertyId] = override
    else delete comparableOverrides[propertyId]

    const updatedValuation = await recalculateValuation({
      comparableOverrides,
      actionId: `override:${propertyId}`,
    })
    if (updatedValuation) setSelectedComparable(null)
  }

  const handleComparablePropertyUpdate = async (propertyId, values) => {
    const actionId = `source:${propertyId}`
    try {
      setRecalculatingPropertyId(actionId)
      setRecalculationError(null)
      await valuationService.updateComparableProperty(propertyId, values)

      const comparableOverrides = { ...(valuation.comparableOverrides || {}) }
      delete comparableOverrides[propertyId]
      const updatedValuation = await recalculateValuation({ comparableOverrides, actionId })
      if (updatedValuation) setSelectedComparable(null)
    } catch (error) {
      console.error('Error updating comparable property:', error)
      setRecalculationError(error.message || 'Nemovitost se nepodařilo upravit')
      setRecalculatingPropertyId(null)
    }
  }

  const handleEditSearchDiameter = () => {
    setSearchDiameterMeters(getValuationSearchRadius(valuation) * 2)
    setRecalculationError(null)
    setEditingSearchDiameter(true)
  }

  const handleCancelSearchDiameter = () => {
    setSearchDiameterMeters(getValuationSearchRadius(valuation) * 2)
    setRecalculationError(null)
    setEditingSearchDiameter(false)
  }

  const handleSaveSearchDiameter = async () => {
    const updatedValuation = await recalculateValuation({
      searchRadiusMeters: searchDiameterMeters / 2,
      actionId: 'search-diameter',
    })
    if (updatedValuation) setEditingSearchDiameter(false)
  }

  const handleGenerateAnalysis = async () => {
    try {
      setGeneratingAnalysis(true)
      setAnalysisError(null)
      const updatedValuation = await valuationService.generateAnalysis(valuationId)
      setValuation(updatedValuation)
      setAnalysisInvalidated(false)
    } catch (error) {
      console.error('Error generating valuation analysis:', error)
      setAnalysisError(error.message || 'Chyba při generování AI analýzy')
    } finally {
      setGeneratingAnalysis(false)
    }
  }

  const handleEnableShare = async () => {
    try {
      setShareBusy(true)
      setShareError(null)
      const publicShare = await valuationService.shareValuation(valuationId)
      setValuation((current) => ({ ...current, publicShare }))
    } catch (error) {
      setShareError(error.message || 'Sdílení se nepodařilo zapnout')
    } finally {
      setShareBusy(false)
    }
  }

  const handleCopyShare = async () => {
    const publicUrl = getOwnerShareUrl(valuation)
    if (!publicUrl) return

    try {
      await navigator.clipboard.writeText(publicUrl)
      setShareCopied(true)
      window.setTimeout(() => setShareCopied(false), 2000)
    } catch (error) {
      setShareError('Odkaz se nepodařilo zkopírovat. Označte jej a zkopírujte ručně.')
    }
  }

  const handleRevokeShare = async () => {
    if (!window.confirm('Opravdu chcete veřejný odkaz zrušit? Starý odkaz přestane fungovat.')) return

    try {
      setShareBusy(true)
      setShareError(null)
      const publicShare = await valuationService.revokeValuationShare(valuationId)
      setValuation((current) => ({ ...current, publicShare }))
      setShareCopied(false)
    } catch (error) {
      setShareError(error.message || 'Sdílení se nepodařilo zrušit')
    } finally {
      setShareBusy(false)
    }
  }

  const handleExportPDF = async () => {
    try {
      const element = printRef.current
      await waitForImages(element)

      // Create canvas from the element with map included
      const canvas = await html2canvas(element, {
        scale: 2, // High quality for crisp text
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        removeContainer: true,
        logging: false,
        ignoreElements: (element) => {
          // Only ignore specific problematic elements if needed
          return element.dataset?.pdfIgnore === 'true' || (element.classList && element.classList.contains('leaflet-control-container'))
        },
      })

      // Create PDF with optimized image
      const imgData = canvas.toDataURL('image/jpeg', 0.8) // Use JPEG with 80% quality
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight() - 40 // Leave space for title
      const imgWidth = canvas.width
      const imgHeight = canvas.height

      // Calculate scaling to fit page
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const scaledWidth = imgWidth * ratio
      const scaledHeight = imgHeight * ratio
      const imgX = (pdfWidth - scaledWidth) / 2

      // Add title
      pdf.setFontSize(20)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Detail odhadu nemovitosti', pdfWidth / 2, 15, { align: 'center' })

      if (valuation?.address) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'normal')
        pdf.text(valuation.address, pdfWidth / 2, 25, { align: 'center' })
      }

      // Add content with proper scaling
      pdf.addImage(imgData, 'JPEG', imgX, 30, scaledWidth, scaledHeight)

      // Save the PDF
      const fileName = `odhad-${valuation?.address || 'nemovitost'}-${new Date().toLocaleDateString('cs-CZ')}.pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Chyba při generování PDF. Zkuste to prosím znovu.')
    }
  }

  const analysisCard = valuation ? (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 sm:mb-6">
        <h2 className="text-sm sm:text-lg font-medium text-gray-900">AI analýza</h2>
        {!publicMode && (
          <button
            type="button"
            disabled={generatingAnalysis}
            onClick={handleGenerateAnalysis}
            className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:bg-gray-400"
          >
            {generatingAnalysis ? 'Generuji…' : valuation.analysis ? 'Regenerovat AI text' : 'Vygenerovat AI text'}
          </button>
        )}
      </div>
      {!publicMode && analysisError && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{analysisError}</div>}
      {!publicMode && analysisInvalidated && !valuation.analysis && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Výpočet byl změněn. Předchozí AI text již neodpovídal aktuálnímu odhadu a byl odstraněn.
        </div>
      )}
      {valuation.analysis ? (
        <div className="space-y-4 text-xs sm:text-sm leading-relaxed">
          <p>{valuation.analysis.summary}</p>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Srovnatelné nabídky</h3>
            <p>{valuation.analysis.comparables}</p>
          </div>
          {valuation.analysis.marketActivity && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Rychlost nabídek</h3>
              <p>{valuation.analysis.marketActivity}</p>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Interpretace výsledku</h3>
            <p>{valuation.analysis.resultInterpretation}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Důvěra odhadu</h3>
            <p>{valuation.analysis.confidenceExplanation}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Závěr</h3>
            <p>{valuation.analysis.conclusion}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Postup ocenění</h3>
            <p>{valuation.analysis.methodology}</p>
          </div>
          {valuation.analysis.status === 'fallback' && <p className="text-xs text-amber-700">Text byl vytvořen lokální šablonou, protože AI analýza nebyla dostupná.</p>}
        </div>
      ) : (
        <p className="text-xs sm:text-sm text-gray-500 italic">AI text zatím nebyl vygenerován. Výpočet odhadu je na AI nezávislý.</p>
      )}
    </div>
  ) : null

  const tabs = [
    {
      id: 'main',
      name: 'Hlavní',
      icon: Info,
      content: valuation ? (
        <>
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            {/* Left Column */}
            <div className="w-full lg:w-1/3 space-y-4 sm:space-y-6">
            {valuation.sourceProperty?.propertyId && (
              <div className="overflow-hidden rounded-lg border border-blue-200 bg-white shadow">
                {valuation.sourceProperty.imageUrl && (
                  <img
                    src={valuation.sourceProperty.imageUrl}
                    alt={valuation.sourceProperty.title || 'Připojená nemovitost'}
                    className="h-44 w-full object-cover"
                  />
                )}
                <div className="p-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-blue-600">Připojená nemovitost</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {valuation.sourceProperty.title || valuation.sourceProperty.location || valuation.address}
                  </div>
                  {valuation.sourceProperty.location && valuation.sourceProperty.location !== valuation.sourceProperty.title && (
                    <div className="mt-1 text-xs text-gray-500">{valuation.sourceProperty.location}</div>
                  )}
                  <a
                    href={
                      publicMode
                        ? valuation.sourceProperty.listingUrl || '#'
                        : `/detail/${encodeURIComponent(valuation.sourceProperty.propertyId)}`
                    }
                    target={publicMode ? '_blank' : undefined}
                    rel={publicMode ? 'noreferrer' : undefined}
                    className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 ${
                      publicMode && !valuation.sourceProperty.listingUrl ? 'pointer-events-none opacity-50' : ''
                    }`}
                  >
                    Otevřít detail nemovitosti
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            )}
            {/* Detaily */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-4 sm:mb-6">Detaily nemovitosti</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Adresa</label>
                  <div className="font-semibold text-gray-900">{valuation.address}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Dispozice</label>
                    <div className="font-semibold text-gray-900">{valuation.layout}</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Výměra</label>
                    <div className="font-semibold text-gray-900">{valuation.m2} m²</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Stav</label>
                    <div className="font-semibold text-gray-900">{valuation.condition}</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Patro</label>
                    <div className="font-semibold text-gray-900">{valuation.floor || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Výtah</label>
                    <div className="font-semibold text-gray-900">{valuation.hasElevator === true ? 'Ano' : valuation.hasElevator === false ? 'Ne' : 'Neuvedeno'}</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Pod střechou</label>
                    <div className="font-semibold text-gray-900">{valuation.underRoof ? 'Ano' : 'Ne'}</div>
                  </div>
                </div>
                {valuation.notes && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Poznámky</label>
                    <div className="font-semibold leading-relaxed text-gray-900">{valuation.notes}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Výsledky */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-4 sm:mb-6">Výsledky odhadu</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Odhadovaná hodnota</label>
                    <div className="text-lg sm:text-2xl font-bold text-green-600 break-words">{valuation.anchorPrice ? Math.round(valuation.anchorPrice).toLocaleString() : 'N/A'} Kč</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Cena za m²</label>
                    <div className="text-lg sm:text-2xl font-bold text-blue-600 break-words">{valuation.anchorPpm2 ? Math.round(valuation.anchorPpm2).toLocaleString() : 'N/A'} Kč/m²</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Důvěra</label>
                    <div className="text-base sm:text-xl font-semibold text-orange-600">{valuation.confidence ? Math.round(valuation.confidence * 100) : 'N/A'}%</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Použité srovnání</label>
                    <div className="text-base sm:text-xl font-semibold text-gray-900">{valuation.compsUsed || 'N/A'}</div>
                    {Number.isFinite(valuation.availableComparables) && valuation.availableComparables > valuation.compsUsed && (
                      <div className="mt-1 text-xs text-gray-500">
                        + {(valuation.availableComparables - valuation.compsUsed).toLocaleString('cs-CZ')} dalších dostupných
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Metoda výpočtu</label>
                    <div className="text-sm font-semibold text-gray-900">
                      {getValuationMethodLabel(valuation.calculationMethod, valuation.calculationMethodLabel)}
                    </div>
                  </div>
                </div>
                {!publicMode && (
                  <div data-pdf-ignore="true" className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-medium text-gray-700">Metoda relevance</div>
                    {valuation.calculationMethod === VALUATION_METHODS.LADDER && (
                      <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-900">
                        Historická žebříčková metoda zůstává uložená, dokud výslovně nevyberete novou metodu.
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SIMILARITY_METHOD_OPTIONS.map((option) => {
                        const selected =
                          isSimilarityMethod(valuation.calculationMethod) &&
                          normalizeSimilarityMethod(valuation.calculationMethod) === option.id
                        return (
                          <button
                            key={option.id}
                            type="button"
                            disabled={Boolean(recalculatingPropertyId)}
                            onClick={() => handleMethodChange(option.id)}
                            className={`rounded-md border px-3 py-2 text-left text-xs ${
                              selected
                                ? 'border-blue-500 bg-blue-50 font-semibold text-blue-800'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                            } disabled:cursor-wait disabled:opacity-50`}
                          >
                            <span className="block">{option.label}</span>
                            <span className="mt-0.5 block text-[10px] font-normal text-gray-500">{option.description}</span>
                          </button>
                        )
                      })}
                    </div>
                    {recalculatingPropertyId?.startsWith('method:') && <div className="mt-2 text-[10px] text-gray-500">Přepočítávám…</div>}
                  </div>
                )}
                {valuation.calculationAdjustments && (
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-950">
                    <div className="font-semibold">Úpravy vážené metody</div>
                    {valuation.calculationAdjustments.aggregationMethod === 'weighted_average' ? (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <span>
                          Výchozí vážený průměr: {Math.round(valuation.calculationAdjustments.rawWeightedAveragePpm2).toLocaleString()} Kč/m²
                        </span>
                        {Number.isFinite(valuation.calculationAdjustments.marketSpeedAdjustedWeightedAveragePpm2) && (
                          <span>
                            Po úpravě rychlosti: {Math.round(valuation.calculationAdjustments.marketSpeedAdjustedWeightedAveragePpm2).toLocaleString()} Kč/m²
                          </span>
                        )}
                        <span>
                          Po úpravě výměry: {Math.round(valuation.calculationAdjustments.areaAdjustedWeightedAveragePpm2).toLocaleString()} Kč/m²
                        </span>
                        <span>Faktor podlaží: {Math.round(valuation.calculationAdjustments.subjectFloorFactor * 100)} %</span>
                        <span>
                          Po úpravě podlaží: {Math.round(valuation.calculationAdjustments.finalWeightedAveragePpm2).toLocaleString()} Kč/m²
                        </span>
                        <span>
                          Kontrolní vážený medián: {Math.round(valuation.calculationAdjustments.diagnosticWeightedMedianPpm2).toLocaleString()} Kč/m²
                        </span>
                        {valuation.calculationAdjustments.marketSpeedAdjustment && (
                          <span>
                            Rychlost upravena u {valuation.calculationAdjustments.marketSpeedAdjustment.adjustedComparables} srovnání
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <span>Výchozí medián: {Math.round(valuation.calculationAdjustments.rawWeightedMedianPpm2).toLocaleString()} Kč/m²</span>
                        <span>Po úpravě výměry: {Math.round(valuation.calculationAdjustments.areaAdjustedMedianPpm2).toLocaleString()} Kč/m²</span>
                        <span>Faktor podlaží: {Math.round(valuation.calculationAdjustments.subjectFloorFactor * 100)} %</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Vytvořeno</label>
                    <div className="font-semibold text-gray-900">{new Date(valuation.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Poslední aktualizace</label>
                    <div className="font-semibold text-gray-900">{valuation.updatedAt ? new Date(valuation.updatedAt).toLocaleDateString() : 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profesionální analýza */}
            {!publicMode && analysisCard}
          </div>

            {/* Right Column */}
            <div className="w-full lg:w-2/3 space-y-4 sm:space-y-6">
            {/* Mapa */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 sm:mb-6">
                <div>
                  <h2 className="text-sm sm:text-lg font-medium text-gray-900">Mapa</h2>
                  <p className="mt-1 text-xs text-gray-500">
                    Použitý průměr: {(getValuationUsedSearchRadius(valuation) * 2).toLocaleString('cs-CZ')} m
                  </p>
                  {valuation.calculationAdjustments?.adaptiveRadius && (
                    <p className="mt-1 text-[10px] text-gray-500">
                      {valuation.calculationAdjustments.adaptiveRadius.eligibleComparables.toLocaleString('cs-CZ')} dostupných ·{' '}
                      {valuation.calculationAdjustments.adaptiveRadius.highRelevanceComparables.toLocaleString('cs-CZ')} s relevancí ≥{' '}
                      {Math.round(valuation.calculationAdjustments.adaptiveRadius.highRelevanceThreshold * 100)} %
                    </p>
                  )}
                  {getValuationUsedSearchRadius(valuation) < getValuationSearchRadius(valuation) && (
                    <p className="mt-1 text-[10px] text-gray-400">
                      Nastavené maximum: {(getValuationSearchRadius(valuation) * 2).toLocaleString('cs-CZ')} m
                    </p>
                  )}
                </div>
                {!publicMode && isSimilarityMethod(valuation.calculationMethod) && !editingSearchDiameter && (
                  <button
                    type="button"
                    data-pdf-ignore="true"
                    disabled={Boolean(recalculatingPropertyId)}
                    onClick={handleEditSearchDiameter}
                    className="rounded-md border border-blue-300 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:border-gray-200 disabled:text-gray-400"
                  >
                    Upravit průměr
                  </button>
                )}
              </div>
              {!publicMode && editingSearchDiameter && (
                <div data-pdf-ignore="true" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label htmlFor="valuation-search-diameter" className="text-sm font-semibold text-blue-950">
                      Průměr: {searchDiameterMeters.toLocaleString('cs-CZ')} m
                    </label>
                    <button
                      type="button"
                      disabled={Boolean(recalculatingPropertyId)}
                      onClick={() => setSearchDiameterMeters(DEFAULT_SEARCH_RADIUS_METERS * 2)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                    >
                      Výchozí 3 200 m
                    </button>
                  </div>
                  <input
                    id="valuation-search-diameter"
                    type="range"
                    min={MIN_SEARCH_DIAMETER_METERS}
                    max={MAX_SEARCH_DIAMETER_METERS}
                    step={SEARCH_DIAMETER_STEP_METERS}
                    value={searchDiameterMeters}
                    disabled={Boolean(recalculatingPropertyId)}
                    onChange={(event) => setSearchDiameterMeters(Number(event.target.value))}
                    className="mt-3 w-full accent-blue-600"
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-blue-700">
                    <span>{MIN_SEARCH_DIAMETER_METERS.toLocaleString('cs-CZ')} m</span>
                    <span>{MAX_SEARCH_DIAMETER_METERS.toLocaleString('cs-CZ')} m</span>
                  </div>
                  {recalculationError && <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{recalculationError}</div>}
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      disabled={Boolean(recalculatingPropertyId)}
                      onClick={handleCancelSearchDiameter}
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                    >
                      Zrušit
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(recalculatingPropertyId)}
                      onClick={handleSaveSearchDiameter}
                      className="rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:bg-gray-400"
                    >
                      {recalculatingPropertyId === 'search-diameter' ? 'Přepočítávám…' : 'Uložit a přepočítat'}
                    </button>
                  </div>
                </div>
              )}
              <div className="h-72 sm:h-96">
                <ListingMap
                  valuation={valuation}
                  showComparables={true}
                  searchRadiusMeters={editingSearchDiameter ? searchDiameterMeters / 2 : getValuationUsedSearchRadius(valuation)}
                  highlightedPropertyId={highlightedComparableId}
                  onComparableClick={handleOpenComparable}
                />
              </div>
            </div>

            {/* Použitá srovnání */}
            <ValuationComparablesSection
              valuation={valuation}
              viewMode={viewMode}
              recalculationError={recalculationError}
              recalculatingPropertyId={recalculatingPropertyId}
              onResetExclusions={handleResetExclusions}
              onResetWeightOverrides={handleResetWeightOverrides}
              onIgnoreProperty={handleIgnoreProperty}
              onWeightOverride={handleWeightOverride}
              onOpenProperty={handleOpenComparable}
              onHoverProperty={setHighlightedComparableId}
            />
            </div>
          </div>
          {publicMode && <div className="mt-4 sm:mt-6">{analysisCard}</div>}
        </>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          {/* Left Column Loading */}
          <div className="w-full lg:w-1/2 space-y-4 sm:space-y-6">
            <div className="hidden sm:block bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-6"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-6"></div>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="h-8 bg-gray-200 rounded"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="h-6 bg-gray-200 rounded"></div>
                    <div className="h-6 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-6"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column Loading */}
          <div className="w-full lg:w-1/2 space-y-4 sm:space-y-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-6"></div>
                <div className="h-72 sm:h-96 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-6"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    ...(!publicMode
      ? [
          {
            id: 'settings',
            name: 'Nastavení',
            icon: Settings,
            content: (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-6">Nastavení</h2>
                <p className="text-gray-500 italic">Zatím prázdné.</p>
              </div>
            ),
          },
        ]
      : []),
  ]

  const isShared = valuation?.publicShare?.enabled === true
  const publicShareUrl = getOwnerShareUrl(valuation)

  // Create header content for the Layout
  const headerContent = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 sm:gap-4">
        <button onClick={() => navigate('/valuations')} className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
          <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-base sm:text-2xl font-semibold text-gray-900">Detail odhadu</h1>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-2 text-xs font-medium ${
            isShared ? 'border-green-200 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-600'
          }`}
          title={isShared ? 'Tento odhad je dostupný přes veřejný odkaz' : 'Tento odhad není veřejně sdílený'}
        >
          {isShared ? <Globe2 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          <span className="hidden lg:inline">{isShared ? 'Sdílené' : 'Soukromé'}</span>
        </div>
        <button
          type="button"
          onClick={() => {
            setShareError(null)
            setShareDialogOpen(true)
          }}
          disabled={!valuation}
          className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
        >
          <Share2 size={16} />
          <span className="hidden sm:inline">{isShared ? 'Spravovat sdílení' : 'Sdílet odhad'}</span>
          <span className="sm:hidden">Sdílet</span>
        </button>
        <button
          onClick={handleExportPDF}
          disabled={!valuation}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs sm:text-base"
        >
          <Download size={16} />
          <span className="hidden sm:inline">Export PDF</span>
          <span className="sm:hidden">PDF</span>
        </button>
        <button onClick={() => navigate(`/valuations/${valuationId}/edit`)} className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-base">
          Upravit
        </button>
      </div>
    </div>
  )

  const renderShell = (content) =>
    publicMode ? (
      <PublicValuationLayout valuation={valuation} viewMode={viewMode}>{content}</PublicValuationLayout>
    ) : (
      <Layout headerContent={headerContent}>{content}</Layout>
    )

  if (loading) {
    return renderShell(<div className="flex h-64 items-center justify-center">Načítání...</div>)
  }

  if (error) {
    return renderShell(
      <div className="flex h-64 items-center justify-center px-4">
        <div className="text-center">
          <div className="mb-2 text-red-600">{error}</div>
          <button onClick={fetchValuation} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
            Zkusit znovu
          </button>
        </div>
      </div>
    )
  }

  if (!valuation) {
    return renderShell(<div className="flex h-64 items-center justify-center">Odhad nebyl nalezen</div>)
  }

  const activeTabData = tabs.find((tab) => tab.id === activeTab)

  const shareDialog = !publicMode && shareDialogOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="presentation" onMouseDown={() => setShareDialogOpen(false)}>
      <div
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="valuation-share-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="valuation-share-title" className="text-lg font-semibold text-gray-900">
              Sdílení odhadu
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {isShared
                ? 'Každý s tímto odkazem uvidí aktuální verzi odhadu pouze ke čtení.'
                : 'Vytvořte neveřejný odkaz pro klienta. Odkaz můžete kdykoliv zrušit.'}
            </p>
          </div>
          <button type="button" onClick={() => setShareDialogOpen(false)} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800" aria-label="Zavřít">
            <X className="h-5 w-5" />
          </button>
        </div>

        {shareError && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{shareError}</div>}

        {isShared ? (
          <>
            <label htmlFor="valuation-share-url" className="mb-2 block text-xs font-medium text-gray-700">
              Veřejný odkaz
            </label>
            <input
              id="valuation-share-url"
              type="text"
              readOnly
              value={publicShareUrl || ''}
              onFocus={(event) => event.target.select()}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-800"
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyShare}
                disabled={!publicShareUrl}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Copy className="h-4 w-4" />
                {shareCopied ? 'Zkopírováno' : 'Kopírovat odkaz'}
              </button>
              <a
                href={publicShareUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Otevřít
              </a>
              <button
                type="button"
                onClick={handleRevokeShare}
                disabled={shareBusy}
                className="ml-auto rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {shareBusy ? 'Ruším…' : 'Zrušit sdílení'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShareDialogOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Zrušit
            </button>
            <button
              type="button"
              onClick={handleEnableShare}
              disabled={shareBusy}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:bg-gray-400"
            >
              <Share2 className="h-4 w-4" />
              {shareBusy ? 'Vytvářím…' : 'Vytvořit veřejný odkaz'}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const content = (
    <>
      {/* Content */}
      <div className="px-3 sm:px-4 py-4 sm:py-6">
        {!publicMode && location.state?.valuationUpdated && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-xs sm:text-sm text-green-800">Odhad byl aktualizován.</div>
        )}
        {!publicMode && location.state?.valuationCreated && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-xs sm:text-sm text-green-800">Odhad byl vytvořen.</div>
        )}
        {/* Tabs */}
        {!publicMode && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 border-b border-gray-200">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${tab.id === 'settings' ? 'hidden sm:flex' : 'flex'} items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium rounded-t-lg transition-all duration-200 ${
                      activeTab === tab.id ? 'bg-white text-blue-700 border-b-2 border-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="min-h-96" ref={printRef}>
          {activeTabData?.content}
        </div>
      </div>
      {shareDialog}
      <ComparablePropertyModal
        property={selectedComparable}
        editable={!publicMode && isSimilarityMethod(valuation?.calculationMethod)}
        saving={Boolean(recalculatingPropertyId?.startsWith('override:') || recalculatingPropertyId?.startsWith('source:'))}
        onClose={() => setSelectedComparable(null)}
        onSaveOverride={handleComparableOverride}
        onSaveProperty={handleComparablePropertyUpdate}
      />
    </>
  )

  return renderShell(content)
}

export default ValuationDetail
